import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Container,
  Button,
  Grid,
  Divider,
  Stack,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckIcon from "@mui/icons-material/Check";
import BlazeAIBrand from "./BlazeAIBrand";
import MarketingInfoBar from "./MarketingInfoBar";
import PageMeta from "./PageMeta";

function PlanCard({ plan }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const isMostPopular = Boolean(plan.mostPopular);

  return (
    <Card
      elevation={0}
      sx={{
        position: "relative",
        height: "100%",
        minHeight: { xs: 560, md: 620 },
        borderRadius: "16px",
        overflow: "visible",
        border: "1px solid",
        borderColor: isMostPopular
          ? alpha(theme.palette.primary.main, isDark ? 0.65 : 0.55)
          : alpha(theme.palette.divider, isDark ? 0.7 : 1),
        backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.55 : 0.92),
        backdropFilter: "blur(12px)",
        boxShadow: isMostPopular
          ? `0 0 0 2px ${alpha(theme.palette.primary.main, isDark ? 0.35 : 0.22)}, 0 24px 80px ${alpha(
              theme.palette.primary.main,
              isDark ? 0.16 : 0.10
            )}`
          : isDark
            ? `0 0 0 1px ${alpha(theme.palette.common.black, 0.22)}, 0 18px 60px ${alpha(
                theme.palette.common.black,
                0.55
              )}`
            : `0 0 0 1px ${alpha(theme.palette.common.black, 0.06)}, 0 18px 60px ${alpha(
                theme.palette.common.black,
                0.10
              )}`,
        transition: "transform 0.3s cubic-bezier(0.22, 1, 0.36, 1), box-shadow 0.3s ease, border-color 0.3s ease",
        "&:hover": {
          transform: "translateY(-6px)",
          borderColor: alpha(theme.palette.primary.main, isDark ? 0.6 : 0.45),
          boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, isDark ? 0.4 : 0.28)}, 0 30px 90px ${alpha(
            theme.palette.primary.main,
            isDark ? 0.22 : 0.15
          )}`,
        },
      }}
    >
      {isMostPopular ? (
        <Box
          sx={{
            position: "absolute",
            top: -16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 3,
            px: 2.2,
            py: 0.7,
            borderRadius: 999,
            fontSize: 13,
            fontWeight: 900,
            letterSpacing: 0.4,
            color: "common.white",
            border: "1px solid",
            borderColor: alpha(theme.palette.primary.main, isDark ? 0.8 : 0.7),
            background: isDark
              ? `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.95)}, ${alpha(
                  theme.palette.secondary?.main || theme.palette.primary.main,
                  0.92
                )})`
              : `linear-gradient(90deg, ${alpha(theme.palette.primary.main, 0.92)}, ${alpha(
                  theme.palette.secondary?.main || theme.palette.primary.main,
                  0.9
                )})`,
            boxShadow: `0 8px 26px ${alpha(theme.palette.primary.main, isDark ? 0.38 : 0.28)}`,
          }}
        >
          Most Popular
        </Box>
      ) : null}
      <CardContent
        sx={{
          position: "relative",
          zIndex: 1,
          p: { xs: 3, md: 3.5 },
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 950, mb: 1, textAlign: "center" }}>
          {plan.name}
        </Typography>

        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            gap: 0.75,
            mb: 0.5,
            justifyContent: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontWeight: 900,
              color: theme.palette.primary.main,
              lineHeight: 1.05,
            }}
          >
            {plan.price}
          </Typography>
          {plan.period ? (
            <Typography variant="body2" color="text.secondary">
              {plan.period}
            </Typography>
          ) : null}
        </Box>

        {plan.subtitle ? (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", mb: 1.5 }}
          >
            {plan.subtitle}
          </Typography>
        ) : null}

        <Divider sx={{ my: 2, borderColor: alpha(theme.palette.divider, isDark ? 0.55 : 1) }} />

        <Stack spacing={1.1} sx={{ flexGrow: 1, mt: 1 }}>
          {plan.features.map((feature, i) => (
            <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <CheckIcon fontSize="small" color="success" />
              <Typography variant="body2" sx={{ lineHeight: 1.45 }}>
                {feature}
              </Typography>
            </Box>
          ))}
        </Stack>

        <Box sx={{ mt: 3 }}>
          <Button
            fullWidth
            href="/contact"
            variant={isMostPopular ? "contained" : plan.buttonVariant === "outlined" ? "outlined" : "contained"}
            color="primary"
            sx={
              isMostPopular
                ? {
                    fontWeight: 900,
                    borderRadius: 2,
                    py: 1.4,
                    color: "common.white !important",
                    "& .MuiButton-startIcon, & .MuiButton-endIcon": { color: "common.white !important" },
                    "&:hover": { color: "common.white !important" },
                  }
                : plan.buttonVariant === "outlined"
                  ? {
                      fontWeight: 900,
                      borderRadius: 2,
                      py: 1.4,
                      ...(isDark
                        ? {
                            color: "common.white !important",
                            borderColor: alpha(theme.palette.common.white, 0.25),
                          }
                        : {}),
                    }
                  : {
                      fontWeight: 900,
                      borderRadius: 2,
                      py: 1.4,
                      color: "common.white !important",
                      "&:hover": { color: "common.white !important" },
                    }
            }
          >
            {plan.buttonText}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function PricingPage() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isIos =
    typeof navigator !== "undefined" &&
    /iPad|iPhone|iPod/.test(navigator.userAgent || "");
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  const fadeUp = (delay = 0) => ({
    opacity: mounted ? 1 : 0,
    transform: mounted ? "translateY(0)" : "translateY(24px)",
    transition: `opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms, transform 0.8s cubic-bezier(0.22, 1, 0.36, 1) ${delay}ms`,
  });

  const plans = [
    {
      name: "Free",
      price: "$0",
      period: null,
      subtitle: "2 cloud accounts \u00b7 600 resources",
      features: [
        "Full Workbench (Design \u2192 Code \u2192 Estimate \u2192 Review \u2192 PR)",
        "Multi-cloud support (AWS, Azure, GCP)",
        "Billing sync on all connected accounts",
        "300+ built-in OPA policies included",
        "20 custom OPA policies",
        (
          <>
            <BlazeAIBrand /> (100 requests/month)
          </>
        ),
        "Unlimited IaC plan runs",
        "3 team members",
        "Community support (Slack)",
      ],
      buttonText: "GET STARTED FREE",
      buttonVariant: "contained",
      mostPopular: false,
    },
    {
      name: "Pro",
      price: "$499",
      period: "/mo",
      subtitle: "5 cloud accounts \u00b7 10,000 resources",
      features: [
        "Everything in Free, plus:",
        "25 custom OPA policies",
        (
          <>
            <BlazeAIBrand /> (500 requests/month)
          </>
        ),
        "10 team members",
        "Email support",
      ],
      buttonText: "REQUEST ACCESS",
      buttonVariant: "contained",
      mostPopular: false,
    },
    {
      name: "Business",
      price: "$1,499",
      period: "/mo",
      subtitle: "25 cloud accounts \u00b7 50,000 resources",
      features: [
        "Everything in Pro, plus:",
        "Capacity Planner",
        "Team tag set management",
        "Drift detection + AI remediation",
        "Unlimited custom OPA policies",
        (
          <>
            <BlazeAIBrand /> (2,000 requests/month)
          </>
        ),
        "50 team members",
        "Priority support",
      ],
      buttonText: "REQUEST ACCESS",
      buttonVariant: "contained",
      mostPopular: true,
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: null,
      subtitle: "Unlimited accounts \u00b7 Unlimited resources",
      features: [
        "Everything in Business, plus:",
        "SSO/SAML + advanced RBAC",
        "Audit logging + compliance reporting",
        "Bring your own LLM (private AI)",
        (
          <>
            Unlimited <BlazeAIBrand />
          </>
        ),
        "Self-hosted deployment option",
        "Dedicated support + SLA",
        "Unlimited team members",
        "Custom subdomain (company.infrablazecloud.com)",
      ],
      buttonText: "CONTACT SALES",
      buttonVariant: "outlined",
      mostPopular: false,
    },
  ];

  return (
    <>
    <PageMeta
      title="Pricing — Control Plane for Agent Driven Infrastructure"
      description="InfraBlaze pricing — start free. Multi-cloud inventory, IaC generation, drift remediation, policy-as-code, and BlazeAI on every plan."
    />
    <Box
      component="main"
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        pt: {
          xs: "calc(56px + env(safe-area-inset-top) + 40px)",
          md: "calc(56px + env(safe-area-inset-top) + 64px)",
        },
        pb: 0,
        background: isDark
          ? `radial-gradient(1100px 520px at 15% 20%, ${alpha(
              theme.palette.primary.main,
              0.16
            )}, transparent 55%),
             radial-gradient(900px 520px at 82% 10%, ${alpha(
               theme.palette.secondary?.main || theme.palette.primary.main,
               0.12
             )}, transparent 60%),
             radial-gradient(1000px 600px at 60% 85%, ${alpha(
               theme.palette.primary.main,
               0.10
             )}, transparent 55%)`
          : `radial-gradient(1100px 520px at 15% 20%, ${alpha(
              theme.palette.primary.main,
              0.14
            )}, transparent 55%),
             radial-gradient(900px 520px at 82% 10%, ${alpha(
               theme.palette.secondary?.main || theme.palette.primary.main,
               0.10
             )}, transparent 60%),
             radial-gradient(1000px 600px at 60% 85%, ${alpha(
               theme.palette.primary.main,
               0.08
             )}, transparent 55%)`,
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={{ xs: 4, md: 5 }}>
          <Stack spacing={2} alignItems="center">
            <Typography
              variant="h1"
              sx={{
                ...fadeUp(0),
                fontWeight: 950,
                letterSpacing: -1.0,
                lineHeight: 1.04,
                maxWidth: 900,
                fontSize: { xs: "2.1rem", sm: "2.6rem", md: "3.05rem" },
              }}
            >
              Choose the plan that fits your Cloud
            </Typography>
          </Stack>
        </Box>

        {/* Pricing cards */}
        <Box sx={{ px: { xs: 0, md: 0 } }}>
          {isIos && !isMdUp ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 3.5,
                alignItems: "center",
                pb: 1.5,
                px: "max(12px, env(safe-area-inset-left))",
                pr: "max(12px, env(safe-area-inset-right))",
              }}
            >
              {plans.map((plan) => (
                <Box
                  key={plan.name}
                  sx={{
                    width: "calc(100vw - max(24px, calc(env(safe-area-inset-left) + env(safe-area-inset-right))))",
                    maxWidth: "100%",
                  }}
                >
                  <PlanCard plan={plan} />
                </Box>
              ))}
            </Box>
          ) : (
            <Grid container spacing={{ xs: 3, md: 3 }} alignItems="stretch">
              {plans.map((plan, idx) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  md={3}
                  key={plan.name}
                  sx={{
                    display: "flex",
                    ...fadeUp(400 + idx * 120),
                  }}
                >
                  <PlanCard plan={plan} />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Comparison note */}
        <Box
          sx={{
            mt: 4,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, isDark ? 0.6 : 1),
            backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.45 : 0.9),
            backdropFilter: "blur(10px)",
            p: { xs: 3, md: 4 },
            textAlign: "center",
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            <strong>How we compare:</strong> ControlMonkey starts at $800/mo (AWS only).
            Firefly starts at $699/mo. Spacelift starts at $399/mo. InfraBlaze gives you the
            full Workbench with multi-cloud visual design and real-time cost intelligence — starting at $0.
          </Typography>
        </Box>

        {/* Per Organization explainer */}
        <Box
          sx={{
            mt: 3,
            borderRadius: "12px",
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, isDark ? 0.6 : 1),
            backgroundColor: alpha(theme.palette.background.paper, isDark ? 0.45 : 0.9),
            backdropFilter: "blur(10px)",
            p: { xs: 3, md: 4 },
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" sx={{ fontWeight: 900, mb: 1 }}>
                Per Organization
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Every plan is per organization. All team members share the same workspace, cloud accounts,
                and resources. Each organization gets a dedicated, isolated database — not shared multi-tenant infrastructure.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                href="/contact"
                sx={{
                  fontWeight: 900,
                  borderRadius: 2,
                  py: 1.3,
                  color: "common.white !important",
                  "&:hover": { color: "common.white !important" },
                }}
              >
                Talk to sales
              </Button>
            </Grid>
          </Grid>
        </Box>

        {/* Footnote */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            textAlign: "center",
            mt: 3,
            opacity: 0.65,
            fontStyle: "italic",
            fontSize: "0.75rem",
          }}
        >
          * Only company email domains accepted. Each signup creates an isolated tenant with a dedicated database.
        </Typography>
      </Container>
      <Box sx={{ mt: "auto", pt: { xs: 6, md: 8 } }}>
        <MarketingInfoBar />
      </Box>
    </Box>
    </>
  );
}
